import { isAnon } from '@anm/auth/helpers/authAnon';
import hasAuthAccess from '@anm/auth/helpers/hasAuthAccess';
import { ModalRootWrapper } from '@anm/components/modals/Modal/ModalRoot';
import { EMBED_INTERNAL_PARAM } from '@anm/constants/wave-editor';
import { VideoTemplatesPageProvider } from '@anm/contexts/VideoTemplatesPageContext';
import cachePage from '@anm/helpers/nextjs/server/helpers/cachePage';
import { getParams } from '@anm/helpers/parseParams';
import getTemplatesMetaDescription from '@anm/helpers/videoTemplates/getTemplatesMetaDescription';
import getTemplatesMetaTitle from '@anm/helpers/videoTemplates/getTemplatesMetaTitle';
import { TemplateMetaProps } from '@anm/helpers/videoTemplates/types';
import { NextInitialProps } from 'anm-next';
import compose from 'lodash/fp/compose';
import isEqual from 'lodash/fp/isEqual';
import pick from 'lodash/fp/pick';
import { NextPage } from 'next';
import { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import bootstrapPage, { BootstrapPageProps } from '../HOCs/bootstrapPage';
import injectSagasAndReducers from '../HOCs/injectSagasAndReducers';
import MetaHead from '../components/MetaHead';
import MyProjectsSection, { VIDEO_TEMPLATES_TAB, TAB } from '../components/my-projects/MyProjectsSection';
import TemplateBreadcrumbListSchema from '../components/templates/TemplateBreadcrumbListSchema';
import TemplateVideoObject from '../components/templates/TemplateVideoObject';
import TemplateViewModal from '../components/templates/TemplateViewModal';
import VideoTemplatesSection from '../components/templates/VideoTemplatesSection';
import { TemplateListQuery } from '../helpers/videoTemplates/dispatchCurrentTemplate';
import dispatchGetInitialProps from '../helpers/videoTemplates/dispatchGetInitialPropsNew';
import getPageMetaPreview from '../helpers/videoTemplates/getPageMetaPreview';
import usePageName from '../hooks/usePageName';
import useCheckPageNumber from '../hooks/video-templates/useCheckPageNumber';
import useTemplateListStoreInit from '../hooks/video-templates/useTemplateListStoreInit';
import categoryReducer, { categorySaga, CurrentCategoryState } from '../store/modules/category';
import collapseReducer, { collapseSaga } from '../store/modules/collapse';
import filterReducer from '../store/modules/filter';
import stocksReducer, { stocksSaga } from '../store/modules/stocks';
import subscriptionReducer, { subscriptionSaga } from '../store/modules/subscription';
import videoTemplateReducer, { templateSaga, templateSelectors } from '../store/modules/template';
import videoReducer, { videoSaga } from '../store/modules/video';
import waveEventsReducer, { waveEventsSaga } from '../store/modules/waveEvents';
import userPrefsReducer, { userPrefsSaga } from '../store/modules/userprefs';
import brandsReducer, { brandsSaga } from '../store/modules/brands';

import Page404 from './404';
import videoFolderReducer from '../store/modules/videoFolder';
import { authSelectors } from '../store/modules/auth';

type HOCs = BootstrapPageProps;

type TemplateListPageProps = {
  queryParams: TemplateListQuery;
  currentCategory: CurrentCategoryState;
  title?: string;
  embed?: string;
  description?: string;
};

const metaPreviewSize = { width: 400, height: 300 };

const TemplateListPage: NextPage<TemplateListPageProps & HOCs, {}> = props => {
  const { queryParams, path, embed } = props;
  const [showMyProjectMenu, setMyProjectMenuShow] = useState(false);
  const pageName = usePageName(queryParams);
  const isLoggedIn = useSelector(authSelectors.selectAuthLoginStatus);
  const currentTemplate = useSelector(templateSelectors.selectCurrentTemplate);
  const currentTemplateError = useSelector(templateSelectors.selectCurrentTemplateError);

  const metaPreview = getPageMetaPreview(currentTemplate);

  const metaProps: TemplateMetaProps = {
    pageName,
    currentTemplate,
    pageNumber: queryParams.pageNumber
  };

  const isError = useCheckPageNumber();

  const title = getTemplatesMetaTitle(metaProps);
  const description = getTemplatesMetaDescription(metaProps);

  const isEmbedView = !!embed;
  const isInternalEmbed = embed === EMBED_INTERNAL_PARAM;

  const isLogged = hasAuthAccess() && !isAnon();

  useTemplateListStoreInit(queryParams, isEmbedView);
  useEffect(() => {
    !isInternalEmbed && setMyProjectMenuShow(isLogged);
  }, [isLogged, isInternalEmbed, isLoggedIn]);

  const [activeTab, setActiveTab] = useState<TAB>(VIDEO_TEMPLATES_TAB as TAB);

  if (currentTemplateError?.code === 404) return <Page404 />;
  if (isError) return null;

  return (
    <VideoTemplatesPageProvider value={{ pageName }}>
      <TemplateBreadcrumbListSchema />
      {currentTemplate && (
        <>
          <TemplateVideoObject />
          <ModalRootWrapper zIndex={998}>
            <TemplateViewModal />
          </ModalRootWrapper>
        </>
      )}
      <MetaHead path={path} size={metaPreviewSize} preview={metaPreview} {...{ title, description }} />
      {showMyProjectMenu ? (
        <MyProjectsSection activeTab={activeTab} onTabChange={setActiveTab} />
      ) : (
        <VideoTemplatesSection isNavigation={!isEmbedView} isFooter={!isEmbedView} />
      )}
    </VideoTemplatesPageProvider>
  );
};

const MemorizedTemplateListPage = memo(TemplateListPage, (prevProps, nextProps) => {
  const pickProps = pick(['path', 'embed', 'pageName', 'name']);

  return isEqual(prevProps.queryParams, nextProps.queryParams) && isEqual(pickProps(prevProps), pickProps(nextProps));
});

const MAX_AGE = 3600;

(MemorizedTemplateListPage as typeof TemplateListPage).getInitialProps = (ctx: NextInitialProps<TemplateListQuery>) => {
  const { store, query, res } = ctx;
  cachePage(res, MAX_AGE);
  const params = getParams({
    ...pick(['embed', 'pageName'], query)
  });

  dispatchGetInitialProps({
    res,
    query,
    dispatch: store.dispatch
  });

  const onAsyncDone = () => {
    const currentTemplateError = templateSelectors.selectCurrentTemplateError(store.getState());

    if (res && currentTemplateError?.code === 404) {
      res.statusCode = 404;
    }
  };

  return { queryParams: query, onAsyncDone, ...params };
};

export default compose(
  bootstrapPage,
  injectSagasAndReducers(
    () => [
      ...videoSaga(),
      ...stocksSaga(),
      ...brandsSaga(),
      ...categorySaga(),
      ...collapseSaga(),
      ...templateSaga(),
      ...userPrefsSaga(),
      ...waveEventsSaga(),
      ...subscriptionSaga()
    ],
    {
      videoFolder: videoFolderReducer,
      video: videoReducer,
      filter: filterReducer,
      stocks: stocksReducer,
      brands: brandsReducer,
      userPrefs: userPrefsReducer,
      collapse: collapseReducer,
      category: categoryReducer,
      waveEvents: waveEventsReducer,
      template: videoTemplateReducer,
      subscription: subscriptionReducer
    },
    { id: 'MemorizedTemplateListPage' }
  )
)(MemorizedTemplateListPage);
